import React, { useEffect, useState, useRef } from "react";
import Layout from "@/components/Layout";
import { api, imgUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  IDENTITY_TAG_SUGGESTIONS, PRONOUN_OPTIONS, AGE_RANGES,
  RELATIONSHIP_TYPES, LANGUAGE_SUGGESTIONS, COLLAB_FIELDS,
} from "@/lib/options";
import { COUNTRIES, getStates, getCities, hasCascade } from "@/lib/locations";
import { Trash2, ImagePlus, Eye, EyeOff, Save, AlertTriangle, UserX, UserCheck } from "lucide-react";

function MultiToggle({ options, value, onChange, testidPrefix }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const active = value.includes(o);
        return (
          <button key={o} type="button" data-testid={`${testidPrefix}-${o.toString().toLowerCase().replace(/[^\w]+/g,"-")}`}
            onClick={() => onChange(active ? value.filter((x) => x !== o) : [...value, o])}
            className={`lif-toggle ${active ? "lif-toggle-active" : "hover:bg-mustard"}`}>{o}</button>
        );
      })}
    </div>
  );
}

function Toggle({ checked, onChange, label, testid }) {
  return (
    <button type="button" onClick={()=>onChange(!checked)} data-testid={testid}
      className={`inline-flex items-center gap-3 px-4 py-2.5 rounded-full border-2 border-black transition-all duration-150
        ${checked ? "bg-wine text-white" : "bg-linen text-black hover:bg-mustard"}`}>
      <span className={`w-2 h-2 rounded-full ${checked ? "bg-white" : "bg-black/40"}`}/>
      {label}
    </button>
  );
}

function CheckboxRow({ checked, onChange, label, hint, testid }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer group select-none">
      <span
        className={`mt-0.5 w-5 h-5 rounded border-2 border-black flex items-center justify-center transition-all shrink-0
          ${checked ? "bg-wine" : "bg-linen group-hover:bg-mustard"}`}
      >
        {checked && <span className="w-2 h-2 bg-white rounded-sm" />}
      </span>
      <input
        type="checkbox"
        className="sr-only"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testid}
      />
      <span className="flex-1">
        <span className="text-sm font-medium text-ink leading-snug block">{label}</span>
        {hint && <span className="text-xs text-muted leading-snug block mt-0.5">{hint}</span>}
      </span>
    </label>
  );
}


export default function CoupleDashboard() {
  const { logout } = useAuth();
  const fileRef = useRef();
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [applications, setApplications] = useState([]);
  // Free-entry mode flags for cascading-country state/city dropdowns
  const [stateOtherMode, setStateOtherMode] = useState(false);
  const [cityOtherMode, setCityOtherMode] = useState(false);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/couples/me"); setP(data); } catch {}
      try { const { data } = await api.get("/opportunities/me/applied"); setApplications(data); } catch {}
    })();
  }, []);

  // When profile loads with values that aren't in the cascading lists, switch to free-entry mode
  useEffect(() => {
    if (!p?.location_country || !hasCascade(p.location_country)) return;
    const stateInList = getStates(p.location_country).includes(p.location_state || "");
    if (p.location_state && !stateInList) setStateOtherMode(true);
    if (stateInList && p.location_city) {
      const cityInList = getCities(p.location_country, p.location_state).includes(p.location_city);
      if (!cityInList) setCityOtherMode(true);
    }
  // run only on initial load (when p first arrives)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p?.user_id]);

  if (!p) return <Layout><div className="max-w-3xl mx-auto px-6 py-24 text-muted">Loading…</div></Layout>;

  const set = (k, v) => setP({ ...p, [k]: v });
  const payloadFromState = (state) => ({
    display_name: state.display_name, about_text: state.about_text || "",
    location_city: state.location_city || "", location_state: state.location_state || "",
    location_country: state.location_country || "", travel_radius: state.travel_radius || "",
    open_to_styled_shoots: !!state.open_to_styled_shoots,
    open_to_editorial: !!state.open_to_editorial,
    open_to_content_days: !!state.open_to_content_days,
    pronouns: state.pronouns || "", partner_pronouns: state.partner_pronouns || "",
    additional_lovers: state.additional_lovers || "",
    identity_tags: state.identity_tags || [], cultural_background: state.cultural_background || "",
    accessibility_notes: state.accessibility_notes || "", body_description: state.body_description || "",
    languages: state.languages || [], age_range: state.age_range || "",
    relationship_type: state.relationship_type || "",
    instagram: state.instagram || "", contact_email: state.contact_email || "",
    is_published: !!state.is_published, anonymous_display: !!state.anonymous_display,
  });

  const save = async () => {
    setBusy(true); setSaveMsg("");
    try {
      const { data } = await api.put("/couples/me", payloadFromState(p));
      setP(data);
      setSaveMsg("Saved!"); setTimeout(() => setSaveMsg(""), 2000);
    } catch (e) {
      setSaveMsg(e?.response?.data?.detail || "Error saving.");
    } finally { setBusy(false); }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if ((p.photos || []).length >= 8) { alert("Maximum 8 photos."); return; }
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/couples/me/photos", fd, { headers: { "Content-Type": "multipart/form-data" }});
      setP({ ...p, photos: [...(p.photos||[]), data] });
    } catch (err) {
      alert(err?.response?.data?.detail || "Upload failed.");
    }
    e.target.value = "";
  };

  const removePhoto = async (id) => {
    if (!confirm("Remove this photo?")) return;
    try {
      await api.delete(`/couples/me/photos/${id}`);
      setP({ ...p, photos: (p.photos||[]).filter((x) => x.id !== id) });
    } catch {}
  };

  const togglePublish = async () => {
    const next = !p.is_published;
    setP({ ...p, is_published: next });
    try { await api.put("/couples/me", payloadFromState({ ...p, is_published: next })); } catch {}
  };

  const toggleAnonymous = async () => {
    const next = !p.anonymous_display;
    setP({ ...p, anonymous_display: next });
    try { await api.put("/couples/me", payloadFromState({ ...p, anonymous_display: next })); } catch {}
  };

  const deleteAccount = async () => {
    if (!confirm("This permanently deletes your profile and account. Continue?")) return;
    try {
      await api.delete("/couples/me");
      await logout();
      window.location.href = "/";
    } catch {}
  };

  const aboutWords = (p.about_text || "").trim().split(/\s+/).filter(Boolean).length;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-12 md:py-16">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-10">
          <div>
            <p className="qld-overline mb-2">your profile</p>
            <h1 className="font-serif text-4xl md:text-5xl text-ink">Hey, {(p.display_name||"").split(" ")[0] || "lovely"}.</h1>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <button onClick={togglePublish} data-testid="profile-publish-toggle"
              className={p.is_published ? "qld-btn-secondary" : "qld-btn-coral"}>
              {p.is_published ? <><Eye className="w-4 h-4" strokeWidth={1.5}/> Published</>
                              : <><EyeOff className="w-4 h-4" strokeWidth={1.5}/> Hidden</>}
            </button>
            <button onClick={save} disabled={busy} className="qld-btn-primary" data-testid="profile-save-btn">
              <Save className="w-4 h-4" strokeWidth={1.5}/> {busy ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
        {saveMsg && <p className="mb-6 text-sm bg-mustard border-2 border-black inline-block px-4 py-2 rounded-full" data-testid="profile-save-msg">{saveMsg}</p>}

        {!p.is_published && (
          <div className="mb-8 p-4 rounded-2xl bg-mustard border-2 border-black text-black text-sm flex gap-3">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.5}/>
            <span>Your profile is hidden from the directory. Click "Hidden" above to publish when you're ready.</span>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Photos */}
          <section className="lg:col-span-1">
            <h2 className="font-serif text-2xl text-ink mb-1">Photos</h2>
            <p className="text-sm text-muted mb-4">Up to 8. First photo becomes your cover.</p>
            <div className="grid grid-cols-2 gap-3">
              {(p.photos || []).map((ph) => (
                <div key={ph.id} className="relative aspect-square rounded-2xl overflow-hidden border-2 border-black group" data-testid={`photo-${ph.id}`}>
                  <img src={imgUrl(ph)} alt="" className="w-full h-full object-cover"/>
                  <button onClick={()=>removePhoto(ph.id)} data-testid={`photo-remove-${ph.id}`}
                    className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black text-white border-2 border-black opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Trash2 className="w-4 h-4" strokeWidth={1.5}/>
                  </button>
                </div>
              ))}
              {(p.photos || []).length < 8 && (
                <button onClick={()=>fileRef.current?.click()} data-testid="photo-add-btn"
                  className="aspect-square rounded-2xl border-2 border-dashed border-black bg-linen text-black hover:bg-mustard transition-colors flex flex-col items-center justify-center gap-1">
                  <ImagePlus className="w-6 h-6" strokeWidth={1.5}/>
                  <span className="text-xs">Add photo</span>
                </button>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadPhoto}/>

            <h2 className="font-serif text-2xl text-ink mt-10 mb-3">Privacy</h2>
            <button onClick={toggleAnonymous} data-testid="profile-anon-toggle"
              className={`w-full text-left flex items-center gap-3 p-4 rounded-2xl border-2 border-black transition-all
                ${p.anonymous_display ? "bg-powder" : "bg-linen hover:bg-mustard"}`}>
              {p.anonymous_display ? <UserX className="w-5 h-5" strokeWidth={1.5}/> : <UserCheck className="w-5 h-5" strokeWidth={1.5}/>}
              <div className="text-sm">
                <p className="font-medium">{p.anonymous_display ? "Anonymous display ON" : "Anonymous display OFF"}</p>
                <p className="text-xs text-muted">Hide your name on directory cards & detail page.</p>
              </div>
            </button>
          </section>

          {/* Form */}
          <section className="lg:col-span-2 space-y-8">
            <Field label="Display name">
              <input className="qld-input" value={p.display_name||""} onChange={(e)=>set("display_name", e.target.value)} data-testid="form-display-name"/>
            </Field>

            <Field label="Tell us about you and your relationship - in your own words." hint={`This is the primary thing vendors will read. ${aboutWords > 0 ? aboutWords + " words." : ""}`}>
              <textarea rows={7} className="qld-input" value={p.about_text||""} onChange={(e)=>set("about_text", e.target.value)} data-testid="form-about"
                placeholder="How you met. What you love. What kind of work you'd love to be part of. Whatever you want vendors to know."/>
            </Field>

            <div>
              <h3 className="font-serif text-xl text-ink mb-3">Collaboration preferences</h3>
              <p className="text-xs text-muted mb-3">Pick what you're open to being a part of, our wedding pros will filter by these.</p>
              <div className="flex flex-wrap gap-2">
                {COLLAB_FIELDS.map((c) => (
                  <Toggle key={c.key} label={c.label} checked={!!p[c.key]}
                    onChange={(v)=>set(c.key, v)} testid={`form-${c.key}`}/>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-5">
              <Field label="Country">
                <select className="qld-input" value={p.location_country||""}
                        onChange={(e)=>{
                          setStateOtherMode(false); setCityOtherMode(false);
                          setP({ ...p, location_country: e.target.value, location_state: "", location_city: "" });
                        }}
                        data-testid="form-country">
                  <option value="">Choose…</option>
                  {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="State / Region">
                {hasCascade(p.location_country) && !stateOtherMode ? (
                  <select className="qld-input" value={p.location_state||""}
                          onChange={(e)=>{
                            if (e.target.value === "__other__") {
                              setStateOtherMode(true); setCityOtherMode(true);
                              setP({ ...p, location_state: "", location_city: "" });
                            } else {
                              setP({ ...p, location_state: e.target.value, location_city: "" });
                            }
                          }}
                          data-testid="form-state">
                    <option value="">Choose…</option>
                    {getStates(p.location_country).map((s) => <option key={s} value={s}>{s}</option>)}
                    <option value="__other__">Other (type below)</option>
                  </select>
                ) : (
                  <div className="flex gap-2">
                    <input className="qld-input flex-1" value={p.location_state||""}
                           onChange={(e)=>set("location_state", e.target.value)}
                           placeholder={p.location_country ? "Your state or region" : "Pick a country first"}
                           data-testid="form-state"
                           autoFocus={stateOtherMode}/>
                    {hasCascade(p.location_country) && (
                      <button type="button" onClick={()=>{ setStateOtherMode(false); setCityOtherMode(false); setP({ ...p, location_state: "", location_city: "" }); }}
                              className="text-xs text-wine font-medium underline whitespace-nowrap px-2"
                              data-testid="form-state-back">
                        Back to list
                      </button>
                    )}
                  </div>
                )}
              </Field>
              <Field label="City">
                {hasCascade(p.location_country) && p.location_state && !stateOtherMode && !cityOtherMode ? (
                  <select className="qld-input" value={p.location_city||""}
                          onChange={(e)=>{
                            if (e.target.value === "__other__") {
                              setCityOtherMode(true);
                              setP({ ...p, location_city: "" });
                            } else {
                              set("location_city", e.target.value);
                            }
                          }}
                          data-testid="form-city">
                    <option value="">Choose…</option>
                    {getCities(p.location_country, p.location_state).map((c) => <option key={c} value={c}>{c}</option>)}
                    <option value="__other__">Other (type below)</option>
                  </select>
                ) : (
                  <div className="flex gap-2">
                    <input className="qld-input flex-1" value={p.location_city||""}
                           onChange={(e)=>set("location_city", e.target.value)}
                           placeholder={p.location_country ? "Your town or city" : "Pick a country first"}
                           data-testid="form-city"
                           autoFocus={cityOtherMode && !stateOtherMode}/>
                    {hasCascade(p.location_country) && p.location_state && cityOtherMode && !stateOtherMode && (
                      <button type="button" onClick={()=>{ setCityOtherMode(false); set("location_city", ""); }}
                              className="text-xs text-wine font-medium underline whitespace-nowrap px-2"
                              data-testid="form-city-back">
                        Back to list
                      </button>
                    )}
                  </div>
                )}
              </Field>
            </div>
            <Field label="Travel radius" hint="How far are you happy to travel for a shoot?">
              <input className="qld-input" placeholder="e.g. 100 km · 2 hours · whole state" value={p.travel_radius||""} onChange={(e)=>set("travel_radius", e.target.value)} data-testid="form-travel"/>
            </Field>

            <div className="border-t-2 border-black/15 pt-8">
              <h3 className="font-serif text-2xl text-ink mb-2">Optional identity tags</h3>
              <p className="text-sm text-muted mb-5">
                Everything below is <strong>completely optional</strong>. Skip what doesn't apply. Add what feels true.
              </p>

              <div className="space-y-5">
                <Field label="Your pronouns">
                  <select className="qld-input" value={p.pronouns||""} onChange={(e)=>set("pronouns", e.target.value)} data-testid="form-pronouns">
                    <option value="">Choose or skip</option>
                    {PRONOUN_OPTIONS.map((o) => <option key={o}>{o}</option>)}
                  </select>
                </Field>

                <CheckboxRow
                  checked={!!p.partner_pronouns || p._show_partner}
                  onChange={(v)=>{
                    if (v) { setP({ ...p, _show_partner: true }); }
                    else   { setP({ ...p, _show_partner: false, partner_pronouns: "", additional_lovers: "", _show_more: false }); }
                  }}
                  testid="form-has-partner"
                  label="Add details of another lover"
                  hint="We'll capture their pronouns too."
                />

                {(!!p.partner_pronouns || p._show_partner) && (
                  <div className="ml-7 space-y-5" data-testid="partner-block">
                    <Field label="Their pronouns">
                      <select className="qld-input" value={p.partner_pronouns||""} onChange={(e)=>set("partner_pronouns", e.target.value)} data-testid="form-partner-pronouns">
                        <option value="">Choose or skip</option>
                        {PRONOUN_OPTIONS.map((o) => <option key={o}>{o}</option>)}
                      </select>
                    </Field>

                    <CheckboxRow
                      checked={!!p.additional_lovers || p._show_more}
                      onChange={(v)=>{
                        if (v) { setP({ ...p, _show_more: true }); }
                        else   { setP({ ...p, _show_more: false, additional_lovers: "" }); }
                      }}
                      testid="form-has-more"
                      label="There are more than 3 of us…"
                      hint="Polyamorous, triad, throuple, blended — we hold space for everyone."
                    />

                    {(!!p.additional_lovers || p._show_more) && (
                      <Field label="Please list names & pronouns below">
                        <textarea
                          rows={4}
                          className="qld-input"
                          value={p.additional_lovers||""}
                          onChange={(e)=>set("additional_lovers", e.target.value)}
                          data-testid="form-additional-lovers"
                          placeholder="e.g. Avery (they/them) · Sam (he/they) · Riley (she/her)"
                        />
                      </Field>
                    )}
                  </div>
                )}
              </div>

              <Field label="Identity tags (pick any that apply, skip if not)">
                <MultiToggle options={IDENTITY_TAG_SUGGESTIONS} value={p.identity_tags||[]} onChange={(v)=>set("identity_tags", v)} testidPrefix="form-identity"/>
              </Field>

              <Field label="Cultural / ethnic background" hint="Describe your backgrounds in your own words.">
                <input className="qld-input" value={p.cultural_background||""} onChange={(e)=>set("cultural_background", e.target.value)} data-testid="form-cultural"
                  placeholder="e.g. South Asian + Mexican household"/>
              </Field>

              <Field label="Body / self description" hint="Optional self-description.">
                <input className="qld-input" value={p.body_description||""} onChange={(e)=>set("body_description", e.target.value)} data-testid="form-body"
                  placeholder="e.g. plus-size & proud"/>
              </Field>

              <Field label="Accessibility / disability notes" hint="Anything vendors should know to make a shoot work for you.">
                <input className="qld-input" value={p.accessibility_notes||""} onChange={(e)=>set("accessibility_notes", e.target.value)} data-testid="form-accessibility"
                  placeholder="e.g. wheelchair user · prefer accessible venues"/>
              </Field>

              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Age range">
                  <select className="qld-input" value={p.age_range||""} onChange={(e)=>set("age_range", e.target.value)} data-testid="form-age">
                    <option value="">Skip</option>
                    {AGE_RANGES.map((a) => <option key={a}>{a}</option>)}
                  </select>
                </Field>
                <Field label="Relationship">
                  <select className="qld-input" value={p.relationship_type||""} onChange={(e)=>set("relationship_type", e.target.value)} data-testid="form-relationship">
                    <option value="">Skip</option>
                    {RELATIONSHIP_TYPES.map((o) => <option key={o}>{o}</option>)}
                  </select>
                </Field>
              </div>

              <Field label="Languages spoken">
                <MultiToggle options={LANGUAGE_SUGGESTIONS} value={p.languages||[]} onChange={(v)=>set("languages", v)} testidPrefix="form-lang"/>
              </Field>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <Field label="Instagram handle">
                <input className="qld-input" placeholder="@yourhandle" value={p.instagram||""} onChange={(e)=>set("instagram", e.target.value)} data-testid="form-instagram"/>
              </Field>
              <Field label="Contact email">
                <input type="email" className="qld-input" value={p.contact_email||""} onChange={(e)=>set("contact_email", e.target.value)} data-testid="form-contact-email"/>
              </Field>
            </div>

            <div className="pt-6 border-t-2 border-black/15">
              <h3 className="font-serif text-xl text-ink mb-2">Your applications</h3>
              {applications.length === 0 ? (
                <p className="text-muted text-sm">You haven't applied to any opportunities yet.</p>
              ) : (
                <ul className="divide-y-2 divide-black/15" data-testid="my-applications">
                  {applications.map((a) => (
                    <li key={a.app_id} className="py-3 flex items-center justify-between">
                      <div>
                        <p className="font-medium text-ink">{a.opportunity_title}</p>
                        <p className="text-xs text-muted">Applied {new Date(a.created_at).toLocaleDateString()}</p>
                      </div>
                      <span className="qld-chip qld-chip-sky text-xs">{a.status}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="pt-6 border-t-2 border-black/15">
              <h3 className="font-serif text-xl text-ink mb-2">Danger zone</h3>
              <p className="text-sm text-muted mb-3">Permanently delete your profile and account. This cannot be undone.</p>
              <button onClick={deleteAccount} data-testid="delete-account-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border-2 border-black bg-linen text-wine hover:bg-wine hover:text-white transition-all text-sm font-medium">
                <Trash2 className="w-4 h-4" strokeWidth={1.5}/> Delete my account
              </button>
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block mb-5">
      <span className="block text-sm font-medium text-ink mb-1.5">{label}</span>
      {hint && <span className="block text-xs text-muted mb-2">{hint}</span>}
      {children}
    </label>
  );
}
